/**
 * Sequential multi-tool execution for the Curva MCP surface.
 * Round-trip only: each nested tool keeps its own undo entry.
 */

export const BATCH_TOOLS_NAME = "batch_tools";
export const BATCH_TOOLS_MAX_CALLS = 20;

export interface BatchToolCall {
  name: string;
  arguments?: unknown;
}

export type BatchToolStepResult =
  | { ok: true; name: string; result: unknown }
  | { ok: false; name: string; error: string };

export interface BatchToolsResult {
  ok: boolean;
  completed: number;
  failedAt?: number;
  results: BatchToolStepResult[];
}

export type BatchToolDispatch = (name: string, params: unknown) => Promise<unknown>;

export function isBatchToolsFailureResult(value: unknown): value is BatchToolsResult {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  return record.ok === false && Array.isArray(record.results);
}

export async function runBatchedTools(
  calls: BatchToolCall[],
  dispatch: BatchToolDispatch,
): Promise<BatchToolsResult> {
  if (calls.length === 0) {
    throw new Error("calls must contain at least one tool call.");
  }
  if (calls.length > BATCH_TOOLS_MAX_CALLS) {
    throw new Error(`calls is capped at ${BATCH_TOOLS_MAX_CALLS} (got ${calls.length}).`);
  }

  const results: BatchToolStepResult[] = [];

  for (let index = 0; index < calls.length; index += 1) {
    const call = calls[index]!;
    const name = call.name?.trim();
    if (!name) {
      results.push({ ok: false, name: call.name ?? "", error: "Missing tool name." });
      return { ok: false, completed: results.length - 1, failedAt: index, results };
    }
    if (name === BATCH_TOOLS_NAME) {
      results.push({
        ok: false,
        name,
        error: "Nested batch_tools calls are not allowed.",
      });
      return { ok: false, completed: results.length - 1, failedAt: index, results };
    }

    try {
      const result = await dispatch(name, call.arguments ?? {});
      results.push({ ok: true, name, result });
    } catch (error) {
      results.push({
        ok: false,
        name,
        error: error instanceof Error ? error.message : String(error),
      });
      return { ok: false, completed: results.length - 1, failedAt: index, results };
    }
  }

  return { ok: true, completed: results.length, results };
}

export function parseBatchToolCalls(value: unknown): BatchToolCall[] {
  if (!Array.isArray(value)) {
    throw new Error('Missing array param "calls".');
  }
  return value.map((entry, index) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      throw new Error(`calls[${index}] must be an object.`);
    }
    const record = entry as Record<string, unknown>;
    if (typeof record.name !== "string" || !record.name) {
      throw new Error(`calls[${index}].name must be a non-empty string.`);
    }
    return {
      name: record.name,
      arguments: record.arguments,
    };
  });
}
